import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Handle, Position } from '@xyflow/react';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import {
  Loader2, Network, Users, Compass, MapPin, Book, Calendar,
  Gem, Swords, GitBranch, Search, X, SlidersHorizontal,
  Eye, EyeOff, ChevronDown, Maximize2, ExternalLink,
  Scroll, Star, Sparkles, Shield, Crown, Skull, FileText, Wand2,
} from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';

/* ═══════════════════════════════════════════════════
   Entity type configuration
   ═══════════════════════════════════════════════════ */
const ENTITY_CONFIG = {
  npc:       { icon: Users,     color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.4)',  label: { fr: 'PNJ', en: 'NPC' } },
  quest:     { icon: Compass,   color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.4)',  label: { fr: 'Quête', en: 'Quest' } },
  location:  { icon: MapPin,    color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.4)', label: { fr: 'Lieu', en: 'Location' } },
  lore:      { icon: Book,      color: '#a855f7', bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.4)', label: { fr: 'Encyclopédie', en: 'Lore' } },
  session:   { icon: Calendar,  color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.4)', label: { fr: 'Session', en: 'Session' } },
  treasure:  { icon: Gem,       color: '#06b6d4', bg: 'rgba(6,182,212,0.12)',  border: 'rgba(6,182,212,0.4)',  label: { fr: 'Trésor', en: 'Treasure' } },
  encounter: { icon: Swords,    color: '#f97316', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.4)', label: { fr: 'Rencontre', en: 'Encounter' } },
  narrative: { icon: GitBranch, color: '#ec4899', bg: 'rgba(236,72,153,0.12)', border: 'rgba(236,72,153,0.4)', label: { fr: 'Narratif', en: 'Narrative' } },
};

const LINK_LABELS = {
  mentions: { fr: 'mentionne', en: 'mentions' },
  related_to: { fr: 'lié à', en: 'related to' },
  part_of: { fr: 'fait partie de', en: 'part of' },
  created_by: { fr: 'créé par', en: 'created by' },
  located_in: { fr: 'situé dans', en: 'located in' },
  member_of: { fr: 'membre de', en: 'member of' },
  enemy_of: { fr: 'ennemi de', en: 'enemy of' },
  ally_of: { fr: 'allié de', en: 'ally of' },
  linked: { fr: 'lié', en: 'linked' },
  narrative: { fr: 'narratif', en: 'narrative' },
  main: { fr: 'intrigue principale', en: 'main plot' },
  conditional: { fr: 'conditionnel', en: 'conditional' },
};

/* ═══════════════════════════════════════════════════
   Helper: closest point on segment [A,B] to point P
   ═══════════════════════════════════════════════════ */
function pointToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq)) : 0;
  const cx = ax + t * dx, cy = ay + t * dy;
  const ex = px - cx, ey = py - cy;
  return { dist: Math.sqrt(ex * ex + ey * ey), cx, cy };
}

/* ═══════════════════════════════════════════════════
   Force simulation — minimises edge crossings
   Strategy:
     1. BFS circular initial placement → connected nodes
        start close together, reducing long crossing edges
     2. Node–edge repulsion → each node is pushed away
        from edges it doesn't belong to (direct crossing fix)
     3. Standard node–node repulsion + edge springs
   ═══════════════════════════════════════════════════ */
function computeForceLayout(graphNodes, graphEdges, width, height) {
  const NODE_W = 180;
  const NODE_H = 90;
  const MIN_DIST = Math.sqrt(NODE_W * NODE_W + NODE_H * NODE_H); // ~200px

  const scale = Math.max(1, Math.sqrt(graphNodes.length / 6));
  const W = width * scale;
  const H = height * scale;

  // ── 1. Build adjacency list ──────────────────────
  const adjList = {};
  graphNodes.forEach(n => { adjList[n.id] = []; });
  graphEdges.forEach(e => {
    adjList[e.source]?.push(e.target);
    adjList[e.target]?.push(e.source);
  });

  // ── 2. BFS ordering: connected nodes end up close on the circle ──
  const visited = new Set();
  const bfsOrder = [];
  // Start BFS from the highest-degree node so hubs are well-centred on the circle
  const byDegree = [...graphNodes].sort((a, b) => (adjList[b.id]?.length || 0) - (adjList[a.id]?.length || 0));
  for (const start of byDegree) {
    if (visited.has(start.id)) continue;
    const queue = [start.id];
    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      bfsOrder.push(id);
      adjList[id]?.forEach(nid => { if (!visited.has(nid)) queue.push(nid); });
    }
  }

  // ── 3. Place nodes on a circle in BFS order ─────
  const radius = Math.min(W, H) * 0.38;
  const circlePos = {};
  bfsOrder.forEach((id, i) => {
    const angle = (2 * Math.PI * i) / bfsOrder.length - Math.PI / 2;
    circlePos[id] = {
      x: W / 2 + radius * Math.cos(angle),
      y: H / 2 + radius * Math.sin(angle),
    };
  });

  const nodes = graphNodes.map(n => ({
    ...n,
    x: (circlePos[n.id]?.x ?? W / 2) + (Math.random() - 0.5) * 20,
    y: (circlePos[n.id]?.y ?? H / 2) + (Math.random() - 0.5) * 20,
    vx: 0, vy: 0,
  }));

  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
  const iterations = Math.min(500, Math.max(200, 700 - nodes.length));
  const repulsion = Math.max(8000, 20000 - nodes.length * 20);
  const IDEAL_EDGE_LEN = MIN_DIST * 1.4;
  const NODE_EDGE_RADIUS = MIN_DIST * 1.8; // repulsion radius around each edge

  for (let i = 0; i < iterations; i++) {
    const alpha = Math.max(0.01, 1 - i / iterations);

    // ── Node–node repulsion ──────────────────────
    for (let a = 0; a < nodes.length; a++) {
      for (let b = a + 1; b < nodes.length; b++) {
        const na = nodes[a], nb = nodes[b];
        const dx = na.x - nb.x, dy = na.y - nb.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        let force = (repulsion * alpha) / (dist * dist);
        if (dist < MIN_DIST) force += (MIN_DIST - dist) * 0.8 * alpha;
        const fx = (dx / dist) * force, fy = (dy / dist) * force;
        na.vx += fx; na.vy += fy;
        nb.vx -= fx; nb.vy -= fy;
      }
    }

    // ── Edge spring attraction ───────────────────
    for (const e of graphEdges) {
      const a = nodeMap[e.source], b = nodeMap[e.target];
      if (!a || !b) continue;
      const dx = b.x - a.x, dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - IDEAL_EDGE_LEN) * 0.01 * alpha;
      const fx = (dx / dist) * force, fy = (dy / dist) * force;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }

    // ── Node–edge repulsion (crossing reduction) ─
    // Each node is pushed away from edges it doesn't belong to.
    // This directly reduces the number of crossings.
    if (alpha > 0.04) {
      for (const e of graphEdges) {
        const ea = nodeMap[e.source], eb = nodeMap[e.target];
        if (!ea || !eb) continue;
        for (const n of nodes) {
          if (n.id === e.source || n.id === e.target) continue;
          const { dist, cx, cy } = pointToSegment(n.x, n.y, ea.x, ea.y, eb.x, eb.y);
          if (dist < NODE_EDGE_RADIUS && dist > 0.5) {
            const strength = ((NODE_EDGE_RADIUS - dist) / NODE_EDGE_RADIUS) * 1.8 * alpha;
            n.vx += ((n.x - cx) / dist) * strength * MIN_DIST;
            n.vy += ((n.y - cy) / dist) * strength * MIN_DIST;
          }
        }
      }
    }

    // ── Center pull + damping + integrate ────────
    for (const n of nodes) {
      n.vx += (W / 2 - n.x) * 0.004 * alpha;
      n.vy += (H / 2 - n.y) * 0.004 * alpha;
      n.vx *= 0.8; n.vy *= 0.8;
      n.x += n.vx; n.y += n.vy;
    }

    // ── Overlap resolution ────────────────────────
    for (let a = 0; a < nodes.length; a++) {
      for (let b = a + 1; b < nodes.length; b++) {
        const na = nodes[a], nb = nodes[b];
        const dx = na.x - nb.x, dy = na.y - nb.y;
        const overlapX = NODE_W - Math.abs(dx);
        const overlapY = NODE_H - Math.abs(dy);
        if (overlapX > 0 && overlapY > 0) {
          if (overlapX < overlapY) {
            const push = overlapX / 2 + 5;
            if (dx >= 0) { na.x += push; nb.x -= push; } else { na.x -= push; nb.x += push; }
          } else {
            const push = overlapY / 2 + 5;
            if (dy >= 0) { na.y += push; nb.y -= push; } else { na.y -= push; nb.y += push; }
          }
        }
      }
    }
  }

  return nodes.map(n => ({ id: n.id, x: Math.round(n.x), y: Math.round(n.y) }));
}

/* ═══════════════════════════════════════════════════
   Custom React Flow Node
   ═══════════════════════════════════════════════════ */
function GraphNode({ data, selected }) {
  const cfg = ENTITY_CONFIG[data.type] || ENTITY_CONFIG.lore;
  const Icon = cfg.icon;
  const connectionCount = data.connectionCount || 0;
  const size = connectionCount > 8 ? 'lg' : connectionCount > 3 ? 'md' : 'sm';

  const sizeClasses = {
    sm: 'min-w-[100px] max-w-[140px]',
    md: 'min-w-[120px] max-w-[160px]',
    lg: 'min-w-[140px] max-w-[180px]',
  };

  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !rounded-full !border-0 !opacity-0" />
      <div
        className={cn(
          'group relative rounded-xl px-3 py-2 cursor-pointer transition-all duration-200',
          sizeClasses[size],
          selected && 'ring-2 ring-offset-1 ring-offset-background scale-105',
        )}
        style={{
          background: cfg.bg,
          border: `1.5px solid ${selected ? cfg.color : cfg.border}`,
          boxShadow: selected
            ? `0 0 20px ${cfg.color}40, 0 0 40px ${cfg.color}15`
            : `0 2px 8px rgba(0,0,0,0.15)`,
          ringColor: cfg.color,
        }}
      >
        {/* Glow effect on hover */}
        <div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ boxShadow: `0 0 15px ${cfg.color}30, inset 0 0 15px ${cfg.color}08` }}
        />

        <div className="flex items-center gap-1.5 mb-0.5">
          <div
            className="flex items-center justify-center w-5 h-5 rounded-md shrink-0"
            style={{ background: `${cfg.color}25` }}
          >
            <Icon className="w-3 h-3" style={{ color: cfg.color }} />
          </div>
          <span
            className="font-semibold text-[10px] truncate leading-tight"
            style={{ color: cfg.color }}
            title={data.label}
          >
            {data.label}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[8px] text-muted-foreground/70 uppercase tracking-wider font-medium">
            {cfg.label[data.language] || cfg.label.en}
          </span>
          {connectionCount > 0 && (
            <span className="text-[8px] text-muted-foreground/50 font-mono">
              {connectionCount}
            </span>
          )}
        </div>

        {data.status && (
          <div className="mt-0.5">
            <span className={cn(
              'text-[7px] px-1 py-0.5 rounded-sm font-medium',
              data.status === 'completed' || data.status === 'accomplished' ? 'bg-green-500/20 text-green-400' :
              data.status === 'failed' ? 'bg-red-500/20 text-red-400' :
              data.status === 'in_progress' || data.status === 'available' ? 'bg-blue-500/20 text-blue-400' :
              'bg-muted text-muted-foreground'
            )}>
              {data.status}
            </span>
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !rounded-full !border-0 !opacity-0" />
    </>
  );
}

const nodeTypes = { graph: GraphNode };

/* ═══════════════════════════════════════════════════
   Main page component
   ═══════════════════════════════════════════════════ */
export default function RelationshipGraphPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [savedPositions, setSavedPositions] = useState({});
  const positionsRef = useRef({});

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [hiddenTypes, setHiddenTypes] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`/api/campaigns/${campaignId}/graph`);
        const { positions, ...rest } = res.data;
        setGraphData(rest);
        if (positions && Object.keys(positions).length > 0) {
          setSavedPositions(positions);
          positionsRef.current = positions;
        }
      } catch {
        setGraphData({ nodes: [], edges: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  // Persist positions to backend immediately (called after each drag drop or auto-layout)
  const persistPositions = useCallback((allPositions) => {
    axios.patch(`/api/campaigns/${campaignId}/graph/positions`, {
      positions: allPositions,
    }).catch(() => {});
  }, [campaignId]);

  // Compute connection counts
  const connectionCounts = useMemo(() => {
    if (!graphData) return {};
    const counts = {};
    graphData.edges.forEach(e => {
      counts[e.source] = (counts[e.source] || 0) + 1;
      counts[e.target] = (counts[e.target] || 0) + 1;
    });
    return counts;
  }, [graphData]);

  // Filter nodes
  const filteredNodeIds = useMemo(() => {
    if (!graphData) return new Set();
    return new Set(
      graphData.nodes
        .filter(n => {
          if (hiddenTypes.has(n.type)) return false;
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!n.name.toLowerCase().includes(q) && !n.type.includes(q)) return false;
          }
          return true;
        })
        .map(n => n.id)
    );
  }, [graphData, hiddenTypes, searchQuery]);

  // Force layout positions (computed once per graph data)
  const positions = useMemo(() => {
    if (!graphData || graphData.nodes.length === 0) return {};
    const pos = computeForceLayout(graphData.nodes, graphData.edges, 2000, 1400);
    return Object.fromEntries(pos.map(p => [p.id, { x: p.x, y: p.y }]));
  }, [graphData]);

  // React Flow nodes — saved positions take priority over force layout
  const rfNodes = useMemo(() => {
    if (!graphData) return [];
    return graphData.nodes
      .filter(n => filteredNodeIds.has(n.id))
      .map(n => ({
        id: n.id,
        type: 'graph',
        position: savedPositions[n.id] || positions[n.id] || { x: 0, y: 0 },
        selected: n.id === selectedNodeId,
        data: {
          label: n.name,
          type: n.type,
          sub_type: n.sub_type,
          status: n.status,
          language,
          connectionCount: connectionCounts[n.id] || 0,
        },
      }));
  }, [graphData, filteredNodeIds, positions, savedPositions, language, connectionCounts, selectedNodeId]);

  // React Flow edges
  const rfEdges = useMemo(() => {
    if (!graphData) return [];
    return graphData.edges
      .filter(e => filteredNodeIds.has(e.source) && filteredNodeIds.has(e.target))
      .map((e, i) => {
        const srcType = graphData.nodes.find(n => n.id === e.source)?.type;
        const tgtType = graphData.nodes.find(n => n.id === e.target)?.type;
        const srcColor = ENTITY_CONFIG[srcType]?.color || '#888';
        const tgtColor = ENTITY_CONFIG[tgtType]?.color || '#888';

        // Highlight edges connected to selected node
        const isHighlighted = selectedNodeId && (e.source === selectedNodeId || e.target === selectedNodeId);
        const linkLabel = LINK_LABELS[e.link_type]?.[language] || e.label || '';

        // Dimmed = another node is selected and this edge isn't connected to it
        const isDimmed = selectedNodeId && !isHighlighted;

        return {
          id: `e-${i}-${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          type: 'smoothstep',
          animated: isHighlighted,
          // Label always visible, more prominent when highlighted
          label: linkLabel || undefined,
          labelStyle: {
            fill: isHighlighted ? '#fff' : 'rgba(220,220,230,0.75)',
            fontSize: isHighlighted ? 9 : 7,
            fontWeight: isHighlighted ? 700 : 400,
          },
          labelBgStyle: {
            fill: 'hsl(var(--card))',
            fillOpacity: isHighlighted ? 0.95 : 0.7,
          },
          labelBgPadding: [3, 2],
          labelBgBorderRadius: 3,
          style: {
            // Full color when normal, white+glow when highlighted, very faint when dimmed
            stroke: isHighlighted ? '#ffffff' : srcColor,
            strokeWidth: isHighlighted ? 3 : 1.8,
            opacity: isDimmed ? 0.12 : isHighlighted ? 1 : 0.72,
            filter: isHighlighted ? `drop-shadow(0 0 4px ${tgtColor})` : undefined,
          },
          // Arrow always visible
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isHighlighted ? tgtColor : srcColor,
            width: isHighlighted ? 14 : 10,
            height: isHighlighted ? 14 : 10,
          },
        };
      });
  }, [graphData, filteredNodeIds, language, selectedNodeId]);

  const [nodes, setNodes, onNodesChange] = useNodesState(rfNodes);
  const [edges, setEdges] = useEdgesState(rfEdges);

  React.useEffect(() => { setNodes(rfNodes); }, [rfNodes, setNodes]);
  React.useEffect(() => { setEdges(rfEdges); }, [rfEdges, setEdges]);

  const handleNodeClick = useCallback((_e, node) => {
    setSelectedNodeId(prev => prev === node.id ? null : node.id);
  }, []);

  const handleNodeDoubleClick = useCallback((_e, node) => {
    const type = node.data.type;
    const id = node.id;
    const routes = {
      npc: `npcs/${id}`,
      quest: `quests/${id}`,
      location: `lore?type=location`,
      lore: `lore`,
      session: `sessions/${id}`,
      treasure: `treasure`,
      encounter: `combat`,
      narrative: `narrative`,
    };
    const route = routes[type];
    if (route) navigate(`/campaigns/${campaignId}/${route}`);
  }, [campaignId, navigate]);

  const handleNodeDragStop = useCallback((_e, node) => {
    positionsRef.current = { ...positionsRef.current, [node.id]: node.position };
    setSavedPositions({ ...positionsRef.current });
    persistPositions(positionsRef.current);
  }, [persistPositions]);

  const handleAutoLayout = useCallback(() => {
    if (!graphData) return;
    const computed = computeForceLayout(graphData.nodes, graphData.edges, 2000, 1400);
    const posMap = Object.fromEntries(computed.map(p => [p.id, { x: p.x, y: p.y }]));
    positionsRef.current = posMap;
    setSavedPositions(posMap);
    persistPositions(posMap);
  }, [graphData, persistPositions]);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const toggleType = (type) => {
    setHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  // Gather present entity types
  const presentTypes = useMemo(() => {
    if (!graphData) return [];
    return [...new Set(graphData.nodes.map(n => n.type))].sort();
  }, [graphData]);

  const activeFilterCount = hiddenTypes.size + (searchQuery ? 1 : 0);

  // Selected node info
  const selectedNode = graphData?.nodes.find(n => n.id === selectedNodeId);
  const selectedConnections = useMemo(() => {
    if (!selectedNodeId || !graphData) return [];
    return graphData.edges
      .filter(e => e.source === selectedNodeId || e.target === selectedNodeId)
      .map(e => {
        const otherId = e.source === selectedNodeId ? e.target : e.source;
        const other = graphData.nodes.find(n => n.id === otherId);
        return other ? { ...other, link_type: e.link_type } : null;
      })
      .filter(Boolean);
  }, [selectedNodeId, graphData]);

  return (
    <MainLayout fluid>
      <div className="h-[calc(100vh-4rem)] flex" data-testid="graph-page">
        {/* Graph area */}
        <div className="flex-1 relative">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Chargement du graphe...' : 'Loading graph...'}</p>
              </div>
            </div>
          ) : graphData?.nodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Network className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-lg font-semibold text-muted-foreground mb-1">
                  {language === 'fr' ? 'Aucune relation' : 'No relationships'}
                </p>
                <p className="text-sm text-muted-foreground/60">
                  {language === 'fr'
                    ? 'Créez des PNJ, quêtes et articles pour voir le graphe'
                    : 'Create NPCs, quests and articles to see the graph'}
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full h-full narrative-graph">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onNodeClick={handleNodeClick}
                onNodeDoubleClick={handleNodeDoubleClick}
                onNodeDragStop={handleNodeDragStop}
                onPaneClick={handlePaneClick}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.15, maxZoom: 1.2 }}
                minZoom={0.03}
                maxZoom={2.5}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={30} size={1} color="hsl(var(--border) / 0.25)" />
                <Controls
                  showInteractive={false}
                  className="!bg-card/90 !backdrop-blur-xl !border !border-border/50 !shadow-xl !rounded-xl !overflow-hidden"
                />
                <MiniMap
                  nodeColor={(node) => ENTITY_CONFIG[node.data?.type]?.color || '#888'}
                  maskColor="hsl(var(--background) / 0.85)"
                  className="!bg-card/90 !backdrop-blur-xl !border !border-border/50 !shadow-xl !rounded-xl"
                  pannable
                  zoomable
                  style={{ width: 140, height: 90 }}
                />

                {/* ─── Toolbar ─── */}
                <Panel position="top-left" className="flex flex-col gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                        className="h-8 w-40 sm:w-52 pl-7 pr-7 text-xs rounded-lg bg-card/90 backdrop-blur-xl border border-border/50 shadow-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    {/* Auto-layout button */}
                    <button
                      onClick={handleAutoLayout}
                      title={language === 'fr' ? 'Réorganiser automatiquement' : 'Auto-layout'}
                      className="h-8 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all border shadow-sm bg-card/90 backdrop-blur-xl border-border/50 text-muted-foreground hover:text-foreground"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{language === 'fr' ? 'Auto' : 'Auto'}</span>
                    </button>

                    {/* Filter toggle */}
                    <Popover open={showFilters} onOpenChange={setShowFilters}>
                      <PopoverTrigger asChild>
                        <button className={cn(
                          'h-8 px-2.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all border shadow-sm',
                          activeFilterCount > 0
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'bg-card/90 backdrop-blur-xl border-border/50 text-muted-foreground hover:text-foreground'
                        )}>
                          <SlidersHorizontal className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{language === 'fr' ? 'Filtres' : 'Filters'}</span>
                          {activeFilterCount > 0 && (
                            <span className="w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-bold">
                              {activeFilterCount}
                            </span>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-56 p-3 space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold">{language === 'fr' ? 'Types visibles' : 'Visible types'}</span>
                          {hiddenTypes.size > 0 && (
                            <button onClick={() => setHiddenTypes(new Set())} className="text-[10px] text-destructive hover:underline">
                              {language === 'fr' ? 'Tout afficher' : 'Show all'}
                            </button>
                          )}
                        </div>
                        {presentTypes.map(type => {
                          const cfg = ENTITY_CONFIG[type];
                          if (!cfg) return null;
                          const Icon = cfg.icon;
                          const hidden = hiddenTypes.has(type);
                          const count = graphData.nodes.filter(n => n.type === type).length;
                          return (
                            <button
                              key={type}
                              onClick={() => toggleType(type)}
                              className={cn(
                                'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors',
                                hidden ? 'opacity-40 hover:opacity-60' : 'hover:bg-secondary'
                              )}
                            >
                              {hidden ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3" style={{ color: cfg.color }} />}
                              <Icon className="h-3.5 w-3.5" style={{ color: hidden ? '#888' : cfg.color }} />
                              <span className="flex-1 text-left">{cfg.label[language]}</span>
                              <span className="text-[10px] text-muted-foreground font-mono">{count}</span>
                            </button>
                          );
                        })}
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground bg-card/80 backdrop-blur-sm border border-border/30 rounded-md px-2 py-0.5">
                      {filteredNodeIds.size}/{graphData?.nodes.length || 0} {language === 'fr' ? 'entités' : 'entities'}
                      {' · '}
                      {rfEdges.length} {language === 'fr' ? 'liens' : 'links'}
                    </span>
                  </div>
                </Panel>

                {/* ─── Legend ─── */}
                <Panel position="bottom-left">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="h-7 px-2.5 rounded-lg text-[10px] font-medium flex items-center gap-1.5 bg-card/90 backdrop-blur-xl border border-border/50 shadow-sm text-muted-foreground hover:text-foreground transition-colors">
                        <span className="flex gap-0.5">
                          {presentTypes.slice(0, 5).map(t => (
                            <span key={t} className="w-2 h-2 rounded-full" style={{ background: ENTITY_CONFIG[t]?.color }} />
                          ))}
                        </span>
                        {language === 'fr' ? 'Légende' : 'Legend'}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" side="top" className="w-52 p-3">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                        {language === 'fr' ? 'Types d\'entités' : 'Entity types'}
                      </p>
                      <div className="space-y-1.5">
                        {presentTypes.map(type => {
                          const cfg = ENTITY_CONFIG[type];
                          if (!cfg) return null;
                          const Icon = cfg.icon;
                          return (
                            <div key={type} className="flex items-center gap-2">
                              <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: cfg.color }} />
                              <span className="text-[10px] text-foreground/80">{cfg.label[language]}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="border-t border-border/50 mt-2.5 pt-2.5">
                        <p className="text-[9px] text-muted-foreground/60">
                          {language === 'fr'
                            ? 'Clic = sélectionner · Double-clic = ouvrir'
                            : 'Click = select · Double-click = open'}
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </Panel>
              </ReactFlow>
            </div>
          )}
        </div>

        {/* ─── Detail Panel (when node selected) ─── */}
        {selectedNode && (
          <div className="w-72 border-l border-border bg-card/95 backdrop-blur-xl overflow-y-auto shrink-0 animate-fade-in">
            <div className="p-4 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {(() => {
                    const cfg = ENTITY_CONFIG[selectedNode.type];
                    const Icon = cfg?.icon || Network;
                    return (
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: cfg?.bg, border: `1px solid ${cfg?.border}` }}>
                        <Icon className="w-4 h-4" style={{ color: cfg?.color }} />
                      </div>
                    );
                  })()}
                  <div className="min-w-0">
                    <h3 className="font-bold text-sm truncate">{selectedNode.name}</h3>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {ENTITY_CONFIG[selectedNode.type]?.label[language]}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedNodeId(null)} className="text-muted-foreground hover:text-foreground p-1">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Navigate button */}
              <Button
                size="sm"
                className="w-full text-xs"
                onClick={() => {
                  const routes = {
                    npc: `npcs/${selectedNode.id}`,
                    quest: `quests/${selectedNode.id}`,
                    location: `lore?type=location`,
                    lore: `lore`,
                    session: `sessions/${selectedNode.id}`,
                    treasure: `treasure`,
                    encounter: `combat`,
                    narrative: `narrative`,
                  };
                  const route = routes[selectedNode.type];
                  if (route) navigate(`/campaigns/${campaignId}/${route}`);
                }}
              >
                <ExternalLink className="h-3 w-3 mr-1.5" />
                {language === 'fr' ? 'Ouvrir' : 'Open'}
              </Button>

              {/* Connections */}
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  {language === 'fr' ? 'Connexions' : 'Connections'} ({selectedConnections.length})
                </h4>
                <div className="space-y-1">
                  {selectedConnections.map((conn, i) => {
                    const cfg = ENTITY_CONFIG[conn.type];
                    const Icon = cfg?.icon || Network;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedNodeId(conn.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary transition-colors text-left"
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: cfg?.color }} />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{conn.name}</p>
                          <p className="text-[9px] text-muted-foreground truncate">
                            {LINK_LABELS[conn.link_type]?.[language] || conn.link_type}
                          </p>
                        </div>
                        <ChevronDown className="h-3 w-3 -rotate-90 text-muted-foreground/50 shrink-0" />
                      </button>
                    );
                  })}
                  {selectedConnections.length === 0 && (
                    <p className="text-xs text-muted-foreground/60 italic py-2">
                      {language === 'fr' ? 'Aucune connexion' : 'No connections'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
